import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChurnAnalysis, useGetChurnAnalysis } from "@/lib/api/churn-analysis";
import { formatDate } from "@/lib/utils";
import { Loader2, MessageSquare } from "lucide-react";
import { useMemo } from "react";

interface ChurnAnalysisModalProps {
  mentorshipId: string;
  open: boolean;
  onClose: () => void;
}

export function ChurnAnalysisModal({
  mentorshipId,
  open,
  onClose,
}: ChurnAnalysisModalProps) {
  const {
    data: churnAnalysis,
    isLoading,
    error,
  } = useGetChurnAnalysis(mentorshipId, open);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between pb-4">
          <DialogTitle className="text-xl font-semibold">
            Reason for churn
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600">
              Loading churn analysis...
            </span>
          </div>
        )}

        {error && (
          <div className="py-8 text-center">
            <p className="mb-2 text-red-600">Failed to load churn analysis</p>
            <p className="text-sm text-gray-500">
              {error instanceof Error
                ? error.message
                : "An unknown error occurred"}
            </p>
          </div>
        )}

        {churnAnalysis && (
          <div className="space-y-6">
            <ChurnInfoPanel churnAnalysis={churnAnalysis} />
            <AvaAnalysisSection avaAnalysis={churnAnalysis.avaAnalysis} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ChurnInfoPanelProps {
  churnAnalysis: ChurnAnalysis;
}

function ChurnInfoPanel({ churnAnalysis }: ChurnInfoPanelProps) {
  const createdDate = new Date(churnAnalysis.mentorshipDetails.startDate);

  const durationText = useMemo(() => {
    const churnDate = new Date(churnAnalysis.churnDate);
    if (!churnDate) {
      return "Unknown";
    }

    const durationMs = churnDate.getTime() - createdDate.getTime();
    const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(durationDays / 30);
    const remainingDays = durationDays % 30;

    return months > 0
      ? `${months} month${months !== 1 ? "s" : ""}, ${remainingDays} day${remainingDays !== 1 ? "s" : ""}`
      : `${durationDays} day${durationDays !== 1 ? "s" : ""}`;
  }, [churnAnalysis.churnDate, createdDate]);

  const formatChurnType = (type: string) => {
    switch (type) {
      case "AUTOMATED_DEACTIVATION":
        return "30 day de-activation";
      case "MANUAL_DEACTIVATION":
        return "Manual deactivation";
      case "MENTOR_SWITCH":
        return "Mentor switch";
      default:
        return type.replace(/_/g, " ").toLowerCase();
    }
  };

  return (
    <Card className="p-6">
      <div className="grid grid-cols-4 gap-6">
        <div>
          <h3 className="mb-1 text-sm font-medium text-gray-500">Joined</h3>
          <p className="font-medium">{formatDate(createdDate)}</p>
        </div>

        <div>
          <h3 className="mb-1 text-sm font-medium text-gray-500">Duration</h3>
          <p className="font-medium">{durationText}</p>
        </div>

        <div>
          <h3 className="mb-1 text-sm font-medium text-gray-500">Engagement</h3>
          <div className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4 text-gray-400" />
            <span className="font-medium">
              {churnAnalysis.engagementMetrics.totalMessages} msg
            </span>
          </div>
        </div>

        <div>
          <h3 className="mb-1 text-sm font-medium text-gray-500">Reason</h3>
          <p className="font-medium text-orange-600">
            {formatChurnType(churnAnalysis.churnType)}
          </p>
        </div>
      </div>
    </Card>
  );
}

interface AvaAnalysisSectionProps {
  avaAnalysis: string | null;
}

function AvaAnalysisSection({ avaAnalysis }: AvaAnalysisSectionProps) {
  if (!avaAnalysis) {
    return (
      <Card className="p-6">
        <h3 className="mb-2 text-lg font-semibold text-gray-500">
          AVA reasoning
        </h3>
        <p className="italic text-gray-500">
          No AVA analysis available for this churn.
        </p>
      </Card>
    );
  }

  // Parse the markdown-formatted AVA analysis
  const lines = avaAnalysis.split("\n");
  let title = "";
  let content = "";
  let foundTitle = false;

  for (const line of lines) {
    if (line.startsWith("# ") && !foundTitle) {
      title = line.substring(2).trim();
      foundTitle = true;
    } else if (line.trim() !== "" && line.trim() !== "---" && foundTitle) {
      content += line + " ";
    }
  }

  // If no title found, use first line as title
  if (!title && lines.length > 0) {
    title = lines[0].replace(/^#+\s*/, "").trim();
    content = lines.slice(1).join(" ").replace(/---/g, "").trim();
  }

  return (
    <Card className="bg-gray-50 p-6">
      <h3 className="mb-3 text-sm font-medium text-gray-500">AVA reasoning</h3>

      {title && (
        <h4 className="mb-3 text-lg font-semibold text-black">{title}</h4>
      )}

      {content && (
        <p className="leading-relaxed text-gray-700">{content.trim()}</p>
      )}

      {!title && !content && (
        <p className="italic text-gray-500">Unable to parse AVA analysis.</p>
      )}
    </Card>
  );
}
